import type { CSSProperties } from 'react';
import type { Theme } from '@mui/material/styles';

export type VariantKey = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'q' | 'p2' | 'p3';

type VariantStyles = Record<VariantKey, CSSProperties>;

export const HealthxchangeThemeFontSizes = {
  desktop: {
    xxs: '0.625rem',
    xs: '0.75rem',
    s: '0.875rem',
    m: '1rem',
    l: '1.25rem',
    xl: '1.5rem',
    xxl: '1.875rem',
  },
  mobile: {
    xxs: '0.625rem',
    xs: '0.75rem',
    s: '0.875rem',
    m: '0.875rem',
    l: '1rem',
    xl: '1.125rem',
    xxl: '1.5rem',
  },
};

export const TextVariant = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  p: 'p',
  q: 'q',
  p2: 'p2',
  p3: 'p3',
};

export const MOBILE_TYPOGRAPHY: VariantStyles = {
  h1: {
    fontSize: HealthxchangeThemeFontSizes.mobile.xxl,
    fontWeight: 400,
    lineHeight: '1.875rem',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  h2: {
    fontSize: HealthxchangeThemeFontSizes.mobile.xl,
    fontWeight: 500,
    lineHeight: '1.5rem',
    letterSpacing: '0.0625rem',
    textTransform: 'uppercase',
  },
  h3: {
    fontSize: HealthxchangeThemeFontSizes.mobile.l,
    fontWeight: 700,
    lineHeight: '1.625rem',
    letterSpacing: 0,
  },
  h4: {
    fontSize: HealthxchangeThemeFontSizes.mobile.m,
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  p: {
    fontSize: HealthxchangeThemeFontSizes.mobile.m,
    fontWeight: 500,
    lineHeight: '1.5rem',
    letterSpacing: 0,
  },
  q: {
    fontSize: HealthxchangeThemeFontSizes.mobile.m,
    fontWeight: 500,
    lineHeight: '1.5rem',
    letterSpacing: 0,
    fontStyle: 'italic',
  },
  p2: {
    fontSize: HealthxchangeThemeFontSizes.mobile.xs,
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: 0,
  },
  p3: {
    fontSize: HealthxchangeThemeFontSizes.mobile.xxs,
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: 0,
  },
};

export const buildResponsiveVariant = (theme: Theme, desktop: CSSProperties): CSSProperties => ({
  [theme.breakpoints.up('md')]: desktop,
});

export const DESKTOP_TYPOGRAPHY: VariantStyles = {
  h1: {
    fontSize: HealthxchangeThemeFontSizes.desktop.xxl,
    fontWeight: 300,
    lineHeight: '2.875rem',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  h2: {
    fontSize: HealthxchangeThemeFontSizes.desktop.xl,
    fontWeight: 400,
    lineHeight: '2.5rem',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  h3: {
    fontSize: HealthxchangeThemeFontSizes.desktop.l,
    fontWeight: 700,
    lineHeight: '1.5474999999999999rem',
  },
  h4: {
    fontSize: HealthxchangeThemeFontSizes.desktop.m,
    fontWeight: 500,
    lineHeight: '1.213125rem',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  p: {
    fontSize: HealthxchangeThemeFontSizes.desktop.m,
    fontWeight: 500,
    lineHeight: '1.375rem',
  },
  q: {
    fontSize: HealthxchangeThemeFontSizes.desktop.m,
    fontWeight: 500,
    lineHeight: '1.375rem',
  },
  p2: {
    fontSize: HealthxchangeThemeFontSizes.desktop.s,
    fontWeight: 500,
    lineHeight: '1.375rem',
  },
  p3: {
    fontSize: HealthxchangeThemeFontSizes.desktop.xs,
    fontWeight: 500,
    lineHeight: '0.9099999999999999rem',
  },
};

/**
 * Generate desktop (md+) typography overrides by diffing against the mobile base.
 * Only properties that change from mobile are included (the "delta").
 * This keeps the theme a base so component props (e.g. fontWeight) override cleanly
 * without !important or extra responsive props.
 */
export const buildDesktopResponsiveTypographyOverrides = (
  theme: Theme,
  overrides: VariantStyles = DESKTOP_TYPOGRAPHY,
): Partial<Record<VariantKey, CSSProperties>> =>
  (Object.keys(overrides) as VariantKey[]).reduce(
    (variantToResponsiveOverride, variant) => {
      const mobileStylesForVariant = MOBILE_TYPOGRAPHY[variant];
      const desktopStylesForVariant = overrides[variant];

      const desktopOnlyOverrides: CSSProperties = Object.entries(desktopStylesForVariant).reduce(
        (accumulatedDelta, [cssProperty, desktopValue]) => {
          const propertyKey = cssProperty as keyof CSSProperties;
          const mobileValue = mobileStylesForVariant[propertyKey];
          return mobileValue === desktopValue
            ? accumulatedDelta
            : { ...accumulatedDelta, [propertyKey]: desktopValue };
        },
        {} as CSSProperties,
      );

      return {
        ...variantToResponsiveOverride,
        [variant]: buildResponsiveVariant(theme, desktopOnlyOverrides),
      };
    },
    {} as Partial<Record<VariantKey, CSSProperties>>,
  );
